import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Menu, LogOut, Search, Bell, Sparkles, KeyRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { navForRole } from "../config/nav";
import { ROLES } from "../lib/format";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-2">
      <div className="h-9 w-9 rounded-xl bg-navy-900 flex items-center justify-center shadow-sm">
        <Sparkles className="h-5 w-5 text-white" strokeWidth={2.2} />
      </div>
      <div className="leading-tight">
        <div className="font-display font-bold text-[17px] text-navy-900 tracking-tight">Dermalis</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">CRM Platform</div>
      </div>
    </div>
  );
}

function NavItems({ role, onClick }) {
  const items = navForRole(role);
  return (
    <nav className="flex flex-col gap-0.5 px-3 py-2">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            onClick={onClick}
            data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-navy-900 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100 hover:text-navy-900"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
            <span className="truncate">{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reminderCount, setReminderCount] = useState(0);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const role = user?.role || "bdm";
  const roleMeta = ROLES[role] || {};

  useEffect(() => {
    api.get("/reminders?window=14").then((r) => setReminderCount(r.data.total)).catch(() => {});
  }, []);

  const changePassword = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("Passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPwOpen(false);
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change password");
    } finally {
      setPwBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-white border-r border-slate-200 z-40">
        <div className="h-16 flex items-center border-b border-slate-100">
          <Brand />
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          <NavItems role={role} />
        </div>
        <div className="p-3 border-t border-slate-100">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-navy-900 text-white text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-navy-900 truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{roleMeta.label}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-30 h-16 glass border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5 text-navy-900" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <div className="h-16 flex items-center border-b border-slate-100"><Brand /></div>
              <NavItems role={role} onClick={() => setOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="hidden sm:flex items-center gap-2 flex-1 max-w-md">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                placeholder="Search clinics, contacts, opportunities…"
                className="w-full h-10 pl-9 pr-3 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-slate-300"
                data-testid="global-search"
              />
            </div>
          </div>

          <div className="flex-1 sm:hidden" />

          <div className="flex items-center gap-2">
            <span className={`hidden sm:inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full ${roleMeta.color || "bg-slate-200"}`}>
              {roleMeta.label}
            </span>
            <button onClick={() => navigate("/reminders")} className="p-2 rounded-lg hover:bg-slate-100 relative" data-testid="notifications-btn">
              <Bell className="h-5 w-5 text-slate-600" />
              {reminderCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center" data-testid="reminder-badge">
                  {reminderCount > 99 ? "99+" : reminderCount}
                </span>
              )}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rounded-full" data-testid="user-menu-btn">
                  <Avatar className="h-9 w-9 ring-2 ring-white">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-navy-900 text-white text-xs">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs text-slate-400 font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="change-password-btn">
                  <KeyRound className="h-4 w-4 mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto animate-fade-up">
          {children}
        </main>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><KeyRound className="h-5 w-5 text-brand" /> Change Password</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block">Current Password</label>
              <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password" /></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block">New Password</label>
              <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="Min 8 characters" data-testid="new-password" /></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block">Confirm New Password</label>
              <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="confirm-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
            <Button onClick={changePassword} disabled={pwBusy} className="bg-navy-900 hover:bg-navy-800" data-testid="save-password-btn">
              {pwBusy ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
